/**
 * useTaskSplitView - the GET /tasks/:id split-view read behind the Aufgaben peek and the
 * Bearbeitungsmaske. mock -> taskSplitViewMock.
 *
 * `mockTask` exists because the mock leg has to find the row to echo, and task rows live
 * in more than one fixture: the Maske's come from the Posteingang one, which the
 * AUFGABEN_MOCK lookup cannot find - it silently fell back to AUFGABEN_MOCK[0] and
 * painted an unrelated mail. A caller that already has the row passes it.
 *
 * keepPreviousData: the Maske reads Teil 1 out of this response, so a refetch would
 * blank it (Konzept 4.2/4.7).
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { SplitViewResponse } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { AUFGABEN_MOCK, taskSplitViewMock } from '../mock';
import type { TaskRow } from '../types';

/**
 * The split-view detail for the selected task (live mode only). Returns
 * undefined data while disabled (mock mode) — the peek renders row facts instead.
 */
export function useTaskSplitView(id: string | null, mockTask?: TaskRow | null) {
  const live = isLiveData();
  return useQuery<SplitViewResponse>({
    queryKey: qk.task(id ?? 'none'),
    queryFn: () =>
      live
        ? apiClient.tasks.getSplitView(id as string)
        : Promise.resolve(
            taskSplitViewMock(
              mockTask ?? AUFGABEN_MOCK.find((t) => t.id === id) ?? AUFGABEN_MOCK[0]!,
            ),
          ),
    enabled: id !== null,
    // Detailkonzept 4.7 "Teil 1 ist konstant": the Bearbeitungsmaske reads the Eingang
    // out of this response, so a plain refetch on task selection would blank Teil 1 and
    // its header until the next one lands - exactly the loss of context 4.2 forbids
    // ("Der Benutzer verliert dadurch nie den Bezug zum urspruenglichen Eingang").
    // Holding the previous response keeps Teil 1 painted while Teil 3/4 catch up; every
    // task of one Eingang carries the same communication, so what is held is not stale.
    // The Aufgaben peek gets the same no-flicker behaviour on J/K.
    placeholderData: keepPreviousData,
  });
}
